// As per the initial requirement, the component was named BulkConfigApply.
// It has been updated to include Refresh Config logic as well.

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import OptionalFilters from './OptionalFilters';
import './nodeConfig.less';
import ReusableModal from './ReusableModal';
import EditConfigurationContent from './EditConfigurationContent';
import NodeList from './NodeList';
import NodeConfigurationStatus from './NodeConfigurationStatus';
import { hooks, selectors, actions } from 'farmx-redux-core';
import { useSelector, useDispatch } from 'react-redux';
import {
   getConfigJSON, applyNodeConfig, checkIsValidJSON, fetchDeviceListData,
} from './nodesHelper';

const tableKeysToRender = ['ranch', 'block', 'deviceName', 'deviceIdentifier', 'deviceType', 'lastConfigApplyDate', 
  'updatedAt', 'configSyncStatus'];

// const renderFilters = ['status', 'entity', 'ranch', 'block', 'apply', 'firmwareType']; May be used in the future 
// This will be moved to const file in the future
const statusDropdownOptions = ['success', 'pending', 'none', 'error', 'sync'];
const renderFilters = ['entity', 'deviceType', 'apply'];
const nodeTypes = [
  { value: 'control_node', label: 'Control Node'},
  { value: 'field_node', label: 'Field Node' },
  { value: 'energy_node', label: 'Energy Node' }
];

const { useRanchNames, useBlockNames } = hooks;
const { loadSensorsForEntity, loadNewRanchList, loadNewBlockList } = actions;
const { selectSensorsForEntity } = selectors;


const BulkConfigApplyPage: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [configResult, setConfigResult] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const dispatch = useDispatch()
  const [entityId, setEntityId]=useState<any>(undefined);
  const [deviceList, setDeviceList] = useState<any>([]);
  // const [deviceConfig, setDeviceConfig] = useState('{}');
  // const [showConfigStatus, setShowConfigStatus] = useState(false);
  // const [isConfirmScreen, setIsConfirmScreen] = useState(false);
  const [modalStep, setModalStep] = useState<'edit' | 'apply' | 'status'>('edit');
  const [isFetchingDevices, setIsFetchingDevices] = useState(false);
  const prevTypeRef = useRef<any>(null);


  const [lastUpdated, setLastUpdated] = useState('');
  const [hasFetchedDevices, setHasFetchedDevices] = useState(false);
  const [shouldFetchDevices, setShouldFetchDevices] = useState(false);
  const { t } = useTranslation();

  const fieldNodeSensors = useSelector((state) =>
    selectSensorsForEntity(state, entityId).filter((s:any) => s.sub_type === 'field_node')
  );  
  const ranchIds = fieldNodeSensors.map((s:any) => s.ranch).filter(Boolean);
  const blockIds = fieldNodeSensors.map((s:any)  => s.block).filter(Boolean);

  const ranchNames = useRanchNames(ranchIds);
  const blockNames = useBlockNames(blockIds);
  const getNodeTypeLabel = (type: string) =>
  nodeTypes.find((n) => n.value === type)?.label || type || 'N/A';

  useEffect(() => {
    if (entityId) {
     dispatch(loadSensorsForEntity(entityId))
     dispatch(loadNewRanchList(entityId))
     dispatch(loadNewBlockList(entityId))
   }
  }, [entityId, dispatch]);


  useEffect(() => {
  if (
    shouldFetchDevices &&
    appliedFilters  &&
    fieldNodeSensors.length > 0
  ) {
    fetchDeviceList(appliedFilters);
    setShouldFetchDevices(false);
    setHasFetchedDevices(true);
  }
}, [shouldFetchDevices, appliedFilters, fieldNodeSensors.length, ranchNames, blockNames]);
  
// Reset filters and device related state when entity changes
  useEffect(() => {
    setAppliedFilters(null);        
    setShouldFetchDevices(false);
    setHasFetchedDevices(false);
    setDeviceList([]);
    setSelectedDevices([]);
  }, [entityId]);

  // Clear the table data whenever the device type changes
  useEffect(() => {
    if (
      prevTypeRef.current !== undefined &&
      selectedFilters?.type !== prevTypeRef.current
    ) {
      setDeviceList([]);
      setSelectedDevices([]);
    }
    prevTypeRef.current = selectedFilters?.type;
  }, [selectedFilters?.type]);

  const fetchDeviceList = async (filters) => {
  try {
    setIsFetchingDevices(true);
    const response = await fetchDeviceListData(filters.type, entityId);
    if (!response || response.length === 0) {
      setDeviceList([]);
      setSelectedDevices([]);
      setIsFetchingDevices(false);
      return;
    }

    const updatedDeviceList = response.map((device) => {
      const matchingSensor = fieldNodeSensors.find(
        (sensor:any) => sensor.identifier === device.deviceIdentifier
      );
      const ranchId = matchingSensor?.ranch;
      const blockId = matchingSensor?.block;
      return {
        ...device,
        ranch: ranchNames[ranchId] ?? ranchId,
        block: blockNames[blockId] ?? blockId,
      };
    });
    setDeviceList(updatedDeviceList);
    setLastUpdated(moment().format('YYYY-MM-DD HH:mm:ss'));
    } catch (error) {
      setIsFetchingDevices(false);
    } finally {
      setIsFetchingDevices(false);
    }
  };

  const onNextClick = () => {
    if (!selectedDevices.length) return;
    getConfigJSON(selectedFilters).then((response) => {
      if (response) {
        // setDeviceConfig(JSON.stringify(response));
        setModalStep('apply'); 
        setOpen(true);
      }
    });
  };

  const onFiltersApply = (d: any) => {
    const newFilters = { ...selectedFilters, ...d };   
    setSelectedFilters(newFilters);
    setAppliedFilters(newFilters);
    setHasFetchedDevices(false);
    setShouldFetchDevices(true);
  };

  const onConfigApply = async () => {
    setIsLoading(true);
    const deviceList = selectedDevices?.map((d: any) => d?.deviceIdentifier);
    const result = await applyNodeConfig(deviceList); 
    setConfigResult(result);

    setIsLoading(false);
    fetchDeviceList(appliedFilters)
    setModalStep('status');
  };

  const onOkCancel = () => {
    setOpen(false);
    setModalStep('apply');
  };

  function renderModelContent() {
    switch (modalStep) {
      // which may needed in future
      // case 'edit':
      //   return (
      //     <EditConfigurationContent
      //       nodeCount={selectedDevices?.length}
      //       config={deviceConfig}
      //       onEdit={(d: any) => setDeviceConfig(d)}
      //     />
      //   );

      case 'apply':
        return (
          <div className="refresh-config-modal">
            <span>{t('Total number of devices')}: <b>{selectedDevices?.length}</b></span>
             <span>{t('Node type')}: <b>{getNodeTypeLabel(selectedFilters?.type || selectedDevices?.[0]?.type)}</b></span>
          </div>
        );

      case 'status':
        return (
          <NodeConfigurationStatus
            nodeCount={selectedDevices?.length}
            nodeType={selectedFilters?.type || selectedDevices?.[0]?.type}
            data={selectedDevices}
            filters={selectedFilters}
            configResult={configResult}
          />
        );

      default:
        return null;
    }
  }

  function renderCustomFooter() {
    switch (modalStep) {
      case 'apply':
        return [
          <Button
            key="cancel"
            onClick={() => setOpen(false)}
          >
            {t('Cancel')}
          </Button>,
          <Button
            key="confirm"
            onClick={onConfigApply}
            loading={isLoading}
            type="primary"
          >
            {t('Confirm')}
          </Button>
        ];

   case 'status':
     return [
       <>
        {lastUpdated && (
        <div className="refresh-btn-last-updated">
             <span className='padding-10'>{`${t('Last Updated')}: `}</span>
             <span>{lastUpdated}</span>
          </div>
        )}
        <div className='flex-row justify-content-space-between'>
        <Button
           key="submit"
           type="primary"
           onClick={() => fetchDeviceList(selectedFilters)}
           icon={<ReloadOutlined />}
         >
           {t('Refresh')}
         </Button>
         <Button
           key="done"
           onClick={onOkCancel}
           type="primary"
         >
           {t('Done')}
         </Button>
       </div>
       </>
     ];

      default:
        return [];
    }
  }

  function renderModalTitle() {
    switch (modalStep) {
      case 'apply': return t('Apply Configuration');
      case 'status': return t('Device Configuration Status');
      default: return '';
    }
  }

  return (
    <div className="single-column-page padded desktop-container-config">
      <h1 className='font-bold flex-row justify-content-center reduce-table-space'>
        {t('Refresh Config')}
      </h1>
      <div className="flex-space-between optional-filters-div">
        <OptionalFilters 
          renderItems={renderFilters} 
          onChange={(filters) => {
          setSelectedFilters(filters);
          }}
          onApply={onFiltersApply} 
          statusDropdownOptions={statusDropdownOptions}
          entityId={entityId}
          onEntityChange={setEntityId}
          loading={isFetchingDevices} 
        />
      </div>
      <NodeList
        renderItems={tableKeysToRender}
        onChange={(d: any) => setSelectedDevices(d)} // Update selected devices
        filters={appliedFilters}
        setFilters={setSelectedFilters}
        displayDeviceType
        entityId={entityId}
        dataSource={deviceList} 
        mode='config'
      />
      <div className='flex-row justify-content-space-between margin-top-10'>
        <div className='flex-column gap-10'>
          {lastUpdated && (
            <div className="refresh-btn-last-updated">
              <span>{`${t('Last Updated')}: `}</span>
              <span>{lastUpdated}</span>
            </div>
          )}
          <div className="float-left refresh-btn">
            <Button
              key="submit"
              type="primary"
              onClick={() => fetchDeviceList(appliedFilters)}
              icon={<ReloadOutlined />}
            >
              {t('Refresh')}
            </Button>
          </div>
        </div>
        
        <div className="flex-row justify-content-end">
          <Button
            key="submit"
            type="primary"
            disabled={!selectedDevices.length}
            onClick={() => onNextClick()}
            className='fixed-width-100'
          >
            {t('Next')}
          </Button>
        </div>
      </div>

      {open && (
      <ReusableModal
        title={renderModalTitle()}
        renderContent={renderModelContent()}
        customFooter={renderCustomFooter()}
        showModal={open}
        onCancel={() => onOkCancel()}
        onOk={() => onOkCancel()}
      />
      )}
    </div>
  );
};

export default BulkConfigApplyPage;
